<template>
  <div>
    <collapsible-section
        :id="`objectModelRelationCollapse-${relation.client_id}-${inverse ? 'inverse' : null}-${level}-${dropdownControlId}`"
        control-name="objectModelRelationCollapse"
        :is-open="isOpen(handler.hasRelatedObjectModelMatch(lowerQ, relation))"
        :disabled="relationIsDisabled(relation)"
    >
      <template #section-header>
        <row
          v-tooltip="tooltipMessage(relation)"
        >
          <prefix>
            <icon glyph="object-model"/>
          </prefix>
          <column>
            {{ relation.friendlyDescriptionFor(inverse) }}
          </column>
        </row>
      </template>
      <!--          Don't try to render this section if relation button is disabled-->
      <template #section-contents v-if="!relationIsDisabled(relation)">
        <div
            v-for="relatedOM in relation.relatedObjectModelsFor(inverse)"
            :key="`${relation.client_id}-${inverse ? 'inverse' : null}-${relatedOM.client_id}-${dropdownControlId}`"
        >
          <collapsible-section
              :id="`objectModelRelatedObjectModelCollapse-${relation.client_id}-${inverse ? 'inverse' : null}-${relatedOM.client_id}-${level}-${dropdownControlId}`"
              control-name="objectModelRelatedObjectModelCollapse"
              :is-open="isOpen(handler.hasObjectModelDataMatch(lowerQ, relatedOM))"
          >
            <template #section-header>
              {{ relatedOM.titleOrDefault }}
            </template>
            <template #section-contents>
              <slot></slot>

              <section-fields
                v-if="handler.usersOnly"
                :q="q"
                :handler="handler"
                :section="handler.userSection(relatedOM)"
                icon-glyph="member"
                @select="select"
                :select-additional-parameters="[continuePathFor(relation, relatedOM)]"
              />

              <template v-if="!handler.usersOnly">
                <object-model-property-data
                  v-if="!handler.hideProperties"
                  :handler="handler"
                  :q="q"
                  :object-model="relatedOM"
                  :level="level + 1"
                  default-open
                  :path="continuePathFor(relation, relatedOM)"
                  :parent-tree-context="childTreeContextWithRelation(relation)"
                  @select="select"
                ></object-model-property-data>
                <object-model-record-metadata-data
                  v-if="handler.showRecordMetaData"
                  :handler="handler"
                  :q="q"
                  :object-model="relatedOM"
                  :level="level + 1"
                  default-open
                  @select="select"
                  :path="continuePathFor(relation, relatedOM)"
                  :parent-tree-context="childTreeContextWithRelation(relation)"
                ></object-model-record-metadata-data>
              </template>

              <object-model-relationship-data
                  v-if="level < 5 && handler.traverseRelatedObjectModels"
                  :handler="handler"
                  :q="q"
                  :object-model="relatedOM"
                  :level="level + 1"
                  :path="continuePathFor(relation, relatedOM)"
                  :parent-tree-context="childTreeContextWithRelation(relation)"
                  @select="select"
              ></object-model-relationship-data>
            </template>
          </collapsible-section>
        </div>
      </template>
    </collapsible-section>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import handlerComponentsMixin from "~/components/dynamicFieldsTrees/mixins/handlerComponentsMixin";
import handlerTreeContextMixin from "~/components/dynamicFieldsTrees/mixins/handlerTreeContextMixin";
import CollapsibleSection from './common/collapsible-section.vue';
import ObjectModelPropertyData from "./object-model-property-data.vue";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
import ObjectModel from "~/models/objectModel";
import {ReferencePathNode} from "~/models/referencePaths/referencePathNode";
import {ReferenceNodeType} from "~/enums/referenceNodeType";
import {IDynamicTreeContext} from "~/interfaces/dynamicFieldsInterfaces";
import DynamicFieldsHandler from '~/models/dynamicFields/handlers/dynamicFieldsHandler'
import ObjectModelAccountRelationData from '~/components/dynamicFieldsTrees/handlerComponents/object-model-account-relation-data.vue'
import FieldItem from '~/components/dynamicFieldsTrees/handlerComponents/common/field-item.vue';
import SectionFields from '~/components/dynamicFieldsTrees/handlerComponents/common/section-fields.vue';
import ObjectModelRecordMetadataData from "~/components/dynamicFieldsTrees/handlerComponents/object-model-metadata-data.vue";

export default Vue.extend({
  mixins: [
    handlerComponentsMixin,
    handlerTreeContextMixin,
  ],
  components: {
    ObjectModelAccountRelationData,
    CollapsibleSection,
    ObjectModelPropertyData,
    FieldItem,
    SectionFields,
    ObjectModelRecordMetadataData
  },
  props: {
    handler: {
      // ProcessFlowObjectModelDynamicFieldsHandler | ObjectModelAccountRelationDynamicFieldsHandler
      type: Object as () => DynamicFieldsHandler,
      required: true,
    },
    relation: {
      type: Object as () => ObjectModelRelation,
      required: true,
    },
    inverse: {
      type: Boolean,
      default: false,
    },
    objectModel: {
      type: Object as () => ObjectModel,
      required: true,
    },
    path: {
      type: Array as () => ReferencePathNode[],
      required: true,
    },
    level: {
      type: Number,
      required: true,
    },
  },
  computed: {
    treeContext(): Partial<IDynamicTreeContext> {
      return {
        isTopLevelCollectionAssigned: this.parentTreeContext.isTopLevelCollectionAssigned || this.handler.isTopLevelCollection(this.objectModel, this.parentTreeContext),
        referencePathNodesToLevel: this.path
      }
    },
    userSection() {
      return this.handler.userSection(this.objectModel)
    },
  },
  methods: {
    continuePathFor(relation: ObjectModelRelation, objectModel: ObjectModel): ReferencePathNode[] {
      return [
        ...this.path,
        new ReferencePathNode(null, {reference_type: ReferenceNodeType.ObjectModelRelation, reference_id: relation.id, inverse: this.inverse}),
        new ReferencePathNode(null, {reference_type: ReferenceNodeType.ObjectModel, reference_id: objectModel.id}),
      ];
    },
    relationIsDisabled(relation: ObjectModelRelation): boolean {
      return this.handler.isDisabled(relation, this.handlerContext(relation));
    },
    tooltipMessage(relation: ObjectModelRelation): string {
      return this.handler.tooltipMessage(relation, this.handlerContext(relation))
    },
    childTreeContextWithRelation(relation): Partial<IDynamicTreeContext> {
      return this.childTreeContext({ parent: relation, inverse: this.inverse });
    },
    handlerContext(relation) {
      return {
        ...this.childTreeContextWithRelation(relation),
        inverse: this.inverse
      }
    },
  },
});
</script>